<template>
  <div class="w-full h-full cursor-poiter bg-white flex flex-col rounded-lg relative">
    <div
      class="absolute rounded-full w-10 h-10 -right-4 -top-4 bg-yellow-400 flex items-center justify-center"
      v-if="!showRequestStatus && isTenDaysLeft(detail?.date_finished)"
    >
      <IconWarning />
    </div>

    <div class="rounded-t-lg !h-48 overflow-hidden">
      <img
        v-if="detail?.thumbnail"
        class="h-full w-full object-fill"
        :src="detail.thumbnail"
        alt="business-account-banner"
      />
    </div>

    <div
      class="w-full h-max flex flex-col justify-start items-center bg-white rounded-b-lg flex-1"
      :class="{ 'bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD]': detail?.is_vip }"
    >
      <div class="-mt-3.5 self-end mr-5 z-20 relative">
        <VueStarRating
          :rounded-corners="true"
          :star-size="15"
          active-color="#ffb300"
          inactive-color="#f2f2f2"
          :border-width="3"
          border-color="#ffb300"
          :show-rating="false"
          :read-only="true"
          :increment="0.5"
          :rating="detail?.rating"
        ></VueStarRating>
      </div>

      <div class="p-3 w-full min-w-0">
        <p
          class="text-start w-full text-main-color font-bold text-xl truncate"
          :title="detail?.name"
        >
          {{ detail?.name }}
        </p>

        <p class="text-start w-full text-[#333333] font-light truncate" :title="detail?.caption">
          {{ detail?.caption }}
        </p>

        <div class="flex items-center gap-1 mt-2 min-w-0" v-if="detail?.work_hours">
          <IconClock class="shrink-0" />
          <span class="min-w-0 truncate" :title="detail?.work_hours[getDay.toLowerCase()]">
            {{ detail?.work_hours[getDay.toLowerCase()] }}
          </span>
        </div>

        <div v-if="detail?.location" class="flex items-center gap-1 mt-2 min-w-0">
          <IconMarker class="shrink-0" />
          <span class="min-w-0 truncate" :title="detail?.location">
            {{ detail?.location }}
          </span>
        </div>

        <p
          class="bg-yellow-400 w-full text-center mt-3 px-5 py-1 rounded-md font-semibold text-black"
          v-if="!showRequestStatus && detail?.status === 2"
        >
          {{ $t('page.expired') }}
        </p>

        <p v-if="showRequestStatus && requestStatusMeta" class="mt-3 font-bold text-sm">
          {{ $t('page.status') }}:
          <span :class="requestStatusMeta.class">
            {{ requestStatusMeta.label }}
          </span>
        </p>
      </div>

      <div class="mt-auto w-full">
        <div v-if="!detail?.is_vip && detail?.subscription == ''" class="py-5"></div>

        <div
          v-if="detail?.is_vip"
          class="w-max ml-auto px-3 bg-[#7951F9] text-white font-bold text-2xl text-end"
        >
          VIP
        </div>

        <button
          v-if="detail?.subscription !== ''"
          class="w-full rounded-b-md py-3 font-bold"
          :class="getSubcriptionType?.color"
        >
          {{ getSubcriptionType?.name }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import IconClock from '@icons/IconClock.vue';
import VueStarRating from 'vue-star-rating';
import IconMarker from '@icons/IconMarker.vue';
import IconWarning from '@icons/IconWarning.vue';
import moment from 'moment';

export default {
  name: 'MyBusinessAccountCard',
  props: {
    detail: {
      type: Object,
      default: () => ({}),
    },
    showRequestStatus: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VueStarRating,
    IconClock,
    IconMarker,
    IconWarning,
  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    isTenDaysLeft(dateFinished) {
      if (dateFinished) {
        const date = moment(dateFinished, 'DD.MM.YYYY HH:mm');
        const now = moment();
        const diffInDays = date.diff(now, 'days', true);
        return diffInDays <= 10;
      }
      return false;
    },
  },
  computed: {
    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          return null;
      }
    },

    getDay() {
      if (this.detail?.work_hours) {
        let date = new Date();
        return date.toLocaleString('en-us', { weekday: 'long' });
      }
      return null;
    },

    requestStatusMeta() {
      if (!this.showRequestStatus) return null;

      const locale = this.$i18n?.locale || 'ru';
      const rejectionReason =
        this.detail?.standard_rejection_reason || this.detail?.rejection_reason || '';

      if (rejectionReason) {
        return {
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
          class: 'text-red-500',
        };
      }

      const rawStatus =
        this.detail?.status_code ??
        this.detail?.status ??
        this.detail?.status_value ??
        this.detail?.request_status;

      const rawLabel =
        this.detail?.status_display ??
        this.detail?.status_label ??
        this.detail?.request_status_display ??
        this.detail?.status_name ??
        '';

      const normalizedLabel = String(rawLabel).trim().toLowerCase();

      if (
        rawStatus === 0 ||
        rawStatus === '0' ||
        rawStatus === 1 ||
        rawStatus === '1' ||
        rawStatus === 'pending' ||
        rawStatus === 'waiting' ||
        normalizedLabel.includes('ожида') ||
        normalizedLabel.includes('garaş')
      ) {
        return {
          label: locale === 'tk' ? 'Garaşylýar' : 'Ожидается',
          class: 'text-yellow-500',
        };
      }

      if (
        rawStatus === 3 ||
        rawStatus === '3' ||
        rawStatus === 'rejected' ||
        rawStatus === 'declined' ||
        normalizedLabel.includes('отклон') ||
        normalizedLabel.includes('ret edildi')
      ) {
        return {
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
          class: 'text-red-500',
        };
      }

      return {
        label: locale === 'tk' ? 'Garaşylýar' : 'Ожидается',
        class: 'text-yellow-500',
      };
    },
  },
};
</script>
